/**
 * useComplianceMode
 *
 * Single source of truth for the regulated-entity flag and compliance settings
 * surfaced from the backend. All compliance UI gates on this hook so flipping
 * an org between Standard and Regulated takes effect immediately.
 */

import { useMemo } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRole } from '@/contexts/RoleContext';

export type ComplianceMode = 'standard' | 'regulated';

export interface ComplianceSettings {
  default_expiration_required?: boolean;
  default_expiration_days?: number | null;
  allow_expired_in_communications?: boolean;
  notify_days_before?: number;
  default_template_approval_required?: boolean;
  default_content_approval_required?: boolean;
  default_workflow_approval_required?: boolean;
  approval_levels?: number;
  enforce_consent_on_all_paths?: boolean;
  block_expired_in_communications?: boolean;
  require_approved_template_for_send?: boolean;
  allow_compliance_override?: boolean;
  override_requires_reason?: boolean;
}

export interface UseComplianceModeResult {
  mode: ComplianceMode;
  isRegulated: boolean;
  isDemo: boolean;
  settings: ComplianceSettings | undefined;
  /** True for super_admin / ops_admin / org_admin in the active org. */
  canManageCompliance: boolean;
  /** True for compliance_reviewer (and admins). */
  canApproveContent: boolean;
  /** True for org_admin or above. */
  canOverrideBlock: boolean;
}

const ADMIN_ROLES = new Set(['super_admin', 'ops_admin', 'org_admin']);
const REVIEWER_ROLES = new Set(['super_admin', 'ops_admin', 'org_admin', 'compliance_reviewer']);

export function useComplianceMode(): UseComplianceModeResult {
  const { selectedOrganization } = useOrganization();
  const { user } = useAuth();
  // currentRole reflects the user's role *for the active org* — the right
  // source for org-scoped permission gates. Falls back to user.role only when
  // RoleContext isn't ready yet.
  const { currentRole } = useRole();

  return useMemo(() => {
    const settings = selectedOrganization?.settings || {};
    const mode: ComplianceMode =
      (selectedOrganization as any)?.content_compliance_mode === 'regulated' ||
      settings.content_compliance_mode === 'regulated'
        ? 'regulated'
        : 'standard';
    const isDemo =
      Boolean((selectedOrganization as any)?.is_demo) ||
      Boolean(settings.is_demo);

    const role =
      currentRole ||
      (user as any)?.role ||
      (user as any)?.cbo_role ||
      'staff';

    return {
      mode,
      isRegulated: mode === 'regulated',
      isDemo,
      settings: settings.content_compliance,
      canManageCompliance: ADMIN_ROLES.has(role),
      canApproveContent: REVIEWER_ROLES.has(role),
      canOverrideBlock: ADMIN_ROLES.has(role),
    };
  }, [selectedOrganization, user, currentRole]);
}
